import moment from "moment";

// --- API contract ---
// Change these when the backend contract is final.
const DATE_OF_RETURN_FIELD = "date_of_return";
// Proof document backing the date of return - "Proof of Return" in the UI.
const DOR_ATTACHMENT_FIELD = "dor_attachment";
const DOR_ATTACHMENT_TYPES = ["image/jpeg", "image/png", "application/pdf"];
const DOR_ATTACHMENT_MAX_FILES = 5;
const DOR_ATTACHMENT_MAX_SIZE_MB = 10;
const DOR_ATTACHMENT_MAX_SIZE_BYTES = DOR_ATTACHMENT_MAX_SIZE_MB * 1024 * 1024;
// The backend has no array field for this - it exposes DOR_ATTACHMENT_MAX_FILES
// fixed columns instead: `dor_attachment`, `dor_attachment_2`, ... `_5`.
const DOR_ATTACHMENT_FIELDS = Array.from(
  { length: DOR_ATTACHMENT_MAX_FILES },
  (_, i) => (i === 0 ? DOR_ATTACHMENT_FIELD : `${DOR_ATTACHMENT_FIELD}_${i + 1}`),
);
const COMMERCIAL_ROLES = ["Commercial Admin", "Commercial Staff"];

const isCommercialUser = (userInfo) =>
  COMMERCIAL_ROLES.includes(userInfo?.user?.role);

// WRITE — react-datepicker hands back a Date object; serialise it to the same
// "YYYY-MM-DD" shape the API already uses for date_of_purchase.
const toApiValue = (date) => (date ? moment(date).format("YYYY-MM-DD") : "");

const appendDateOfReturn = (formData, date) => {
  const value = toApiValue(date);
  if (value) formData.append(DATE_OF_RETURN_FIELD, value);
  return formData;
};

// Multiple proof-of-return files share one field name, same as the `media`
// attachments (one FormData entry per file, same key) - the backend splits
// the list back out into its dor_attachment/_2../_5 columns on its side.
const appendDorAttachment = (formData, files) => {
  (files || []).forEach((file) => formData.append(DOR_ATTACHMENT_FIELD, file));
  return formData;
};

// READ — fallback chain covers whichever shape the backend settles on; last
// resort is the ticket created date (customer-created ticket).
const getDateOfReturn = (details) =>
  details?.[DATE_OF_RETURN_FIELD] ||
  details?.user_device?.[DATE_OF_RETURN_FIELD] ||
  details?.created ||
  null;

const formatDateOfReturn = (value) =>
  value ? moment(value).format("DD/MM/YYYY") : "-";

// The backend may return an attachment either as a bare URL string or wrapped
// in an object (the ticket `media` endpoints use `{ media: "<url>" }`), so
// resolve both shapes down to a URL.
const resolveAttachmentUrl = (raw) => {
  if (!raw) return null;
  if (typeof raw === "string") return raw;
  return raw.media || raw.file || raw.url || null;
};

// Reads all populated `dor_attachment`/`dor_attachment_2..5` fields into a
// flat list of URLs, skipping empty ("" or null) slots.
const getDorAttachments = (details) =>
  DOR_ATTACHMENT_FIELDS.map((field) =>
    resolveAttachmentUrl(
      details?.[field] ?? details?.user_device?.[field] ?? null,
    ),
  ).filter(Boolean);

export default {
  DATE_OF_RETURN_FIELD,
  DOR_ATTACHMENT_FIELD,
  DOR_ATTACHMENT_TYPES,
  DOR_ATTACHMENT_MAX_FILES,
  DOR_ATTACHMENT_MAX_SIZE_MB,
  DOR_ATTACHMENT_MAX_SIZE_BYTES,
  DOR_ATTACHMENT_FIELDS,
  isCommercialUser,
  toApiValue,
  appendDateOfReturn,
  appendDorAttachment,
  getDateOfReturn,
  getDorAttachments,
  formatDateOfReturn,
};
